import type { ParameterDefinitionInOut } from '@festina/configuration-general-types/configuration'
import { notEmpty } from '@festina-life/core'

import type { MetaModel } from '../types/metaModel'
import {
  type AbstractTemplate,
  isSubTemplateValueSetupField,
  type SubTemplateValueSetupFieldValue,
  type TemplateVersion,
} from '../types/templateModel'
import { generateEmptyFieldValue } from '../uiModels/createEmptyFieldHelpers'
import { TemplatePathParts } from '../uiModels/templateOperations/helpers'

export const splitPartialTemplatePath = (
  path: string,
): {
  field: { fieldName: string; subTemplateId?: string; index?: string }
  remainingPath?: string
} => {
  const fieldPathRegex =
    /\.(?<fieldName>\w+)(?:\{(?<subTemplateId>.+?)\}|\[(?<index>\d+)\])?/g

  const match = fieldPathRegex.exec(path)

  if (!match) {
    throw new Error(`Could not parse template path ${path}`)
  }

  const { fieldName, subTemplateId, index } = match.groups!

  return {
    field: { fieldName, subTemplateId, index },
    remainingPath:
      fieldPathRegex.lastIndex != path.length
        ? path.substring(fieldPathRegex.lastIndex)
        : undefined,
  }
}

export const splitPathOnId = (path: string) => {
  const match = TemplatePathParts.exec(path)

  if (!match) {
    return undefined
  }

  const { templateId, intermediatePath } = match.groups!

  return [templateId, intermediatePath]
}

export const isFunctionPathDeclaration = (path: string) =>
  path.startsWith('.[function]')

export const getInOutPathFromFullPathOnTemplateVersion = ({
  initialPath,
  templateVersion,
  isShowingEmptyValues,
  metaModel,
}: {
  initialPath: string
  templateVersion: TemplateVersion
  metaModel: MetaModel
  isShowingEmptyValues: boolean
}) => {
  const match = TemplatePathParts.exec(initialPath)

  if (!match) {
    return undefined
  }

  const { templateId, intermediatePath } = match.groups!

  if (templateId !== templateVersion.versionInfo.identifier) {
    throw new Error(
      `Template id ${templateId} does not match the template version name ${templateVersion.versionInfo.identifier}`,
    )
  }

  return [
    `{${templateId}}`,
    ...getInOutPathFromIntermediatePathOnAbstractTemplateVersion({
      intermediatePath,
      abstractTemplate: templateVersion,
      metaModel,
      isShowingEmptyValues,
    }),
  ].join('.')
}

const getInOutPathFromIntermediatePathOnAbstractTemplateVersion = ({
  intermediatePath,
  abstractTemplate,
  metaModel,
  isShowingEmptyValues,
}: {
  intermediatePath: string
  abstractTemplate: AbstractTemplate | TemplateVersion
  metaModel: MetaModel
  isShowingEmptyValues: boolean
}): string[] => {
  if (!intermediatePath) {
    return []
  }

  if (isFunctionPathDeclaration(intermediatePath)) {
    const allFunctionsOnTemplate = [
      ...(abstractTemplate.dynamicFunctions ?? []),
      ...(abstractTemplate.declaredFunctions ?? []),
    ].filter(notEmpty)
    const functionIdInPath = intermediatePath.substring(1)
    const matchingFunction = allFunctionsOnTemplate.find(
      (templateFunction) =>
        templateFunction.functionIdentifier === functionIdInPath,
    )

    return matchingFunction ? [intermediatePath.substring(1)] : []
  }

  const {
    field: { fieldName, subTemplateId, index },
    remainingPath,
  } = splitPartialTemplatePath(intermediatePath)

  if (notEmpty(index)) {
    return [] // Indexed paths are always internal
  }

  if (notEmpty(subTemplateId)) {
    const subTemplateField = abstractTemplate.fields?.find(
      (field) => field.name === fieldName,
    )

    if (!subTemplateField || !isSubTemplateValueSetupField(subTemplateField)) {
      return []
    }

    const remainingPathWithoutDot = remainingPath?.substring(1)
    const doesRemainingPathBelongToSubTemplateValueSetup =
      !remainingPathWithoutDot ||
      isInternalValueSetupPath(remainingPathWithoutDot) ||
      remainingPathWithoutDot === 'cardinality'

    const subTemplateFieldValue =
      subTemplateField.value ??
      (isShowingEmptyValues
        ? (generateEmptyFieldValue({
            field: subTemplateField,
            meta: metaModel,
          }) as SubTemplateValueSetupFieldValue)
        : undefined)

    const indexedSubTemplate = subTemplateFieldValue?.variants.find(
      (variant) => variant?.subTemplateIdentifier === subTemplateId,
    )

    const subTemplatePathPart = `${fieldName}{${subTemplateId}}`

    return !remainingPath ||
      doesRemainingPathBelongToSubTemplateValueSetup ||
      !indexedSubTemplate
      ? [subTemplatePathPart]
      : [
          subTemplatePathPart,
          ...getInOutPathFromIntermediatePathOnAbstractTemplateVersion({
            intermediatePath: remainingPath,
            abstractTemplate: indexedSubTemplate,
            metaModel,
            isShowingEmptyValues,
          }),
        ]
  }

  const property = abstractTemplate.fields?.find(
    (field) => field?.name === fieldName,
  )

  const shouldIncludeProperty = isShowingEmptyValues
    ? !!property
    : !!property?.value

  return shouldIncludeProperty ? [fieldName] : []
}

const isInternalValueSetupPath = (path: string) =>
  ['required', 'requiredInModelInstance'].includes(path) ||
  path.startsWith('validations[')

export const getParameterDetailsFieldFromPath = ({
  entity,
  path,
}: {
  entity: ParameterDefinitionInOut
  path: string
}) => {
  if (!path) {
    return undefined
  }

  // Split on the parameter name rather than on '.' so names containing a dot still match.
  if (!entity.name || !path.startsWith(entity.name)) {
    throw new Error(
      `Parameter name in path ${path} does not match the current parameter name ${entity.name}`,
    )
  }

  const remainingPath = path.substring(entity.name.length)

  if (!remainingPath) {
    return entity
  }

  const dimensionName = remainingPath.substring(1)

  const matchedDimension = entity.dimensionDefinitions?.find(
    (dimension) => dimension?.name === dimensionName,
  )

  if (!matchedDimension) {
    throw new Error(
      `Could not find dimension ${dimensionName} on parameter ${entity.name}`,
    )
  }

  return matchedDimension
}
